import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge } from '@components';
import { UserTableItem } from '../types';

interface UsersTableProps {
  users: UserTableItem[];
  onViewUser: (id: string) => void;
  onDeleteUser: (id: string) => void;
  onCreateUser?: () => void;
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  onViewUser,
  onDeleteUser,
  onCreateUser,
}) => {
  const { t } = useTranslation();

  const getRoleBadge = (user: UserTableItem) => {
    const roleLower = user.role?.toLowerCase();
    const label = user.roleDisplay || t(`users.${roleLower}`) || user.role;
    switch (roleLower) {
      case 'admin':
        return (
          <Badge variant="default" size="sm">
            {label}
          </Badge>
        );
      case 'owner':
        return (
          <Badge variant="default" size="sm">
            {label}
          </Badge>
        );
      case 'manager':
        return (
          <Badge variant="info" size="sm">
            {label}
          </Badge>
        );
      case 'user':
        return (
          <Badge variant="secondary" size="sm">
            {label}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" size="sm">
            {label}
          </Badge>
        );
    }
  };

  const getEmailVerificationBadge = (isVerified: boolean) => {
    return (
      <Badge variant={isVerified ? 'success' : 'warning'} size="sm">
        {isVerified ? t('common.yes') : t('common.no')}
      </Badge>
    );
  };

  return (
    <Card>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  <input type="checkbox" className="rounded border-gray-300 dark:border-gray-600" />
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('users.firstName')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('users.lastName')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('users.email')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('users.role')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('users.emailVerified')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('users.joinedAt')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12">
                    <div className="text-center space-y-4">
                      <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                        <span className="material-icons text-2xl text-gray-400 dark:text-gray-500">
                          people
                        </span>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t('users.noUsersFound')}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                          {t('users.noUsersFoundDescription')}
                        </p>
                      </div>
                      <div className="pt-2">
                        <Button
                          variant="gradient-multi"
                          size="sm"
                          onClick={onCreateUser}
                          className="inline-flex items-center gap-2"
                          disabled
                        >
                          <span className="material-icons text-sm">person_add</span>
                          {t('users.inviteTeamMember')}
                        </Button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map(user => (
                  <tr
                    key={user.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {user.firstName}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {user.lastName}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {user.email}
                    </td>
                    <td className="py-3 px-4">{getRoleBadge(user)}</td>
                    <td className="py-3 px-4">{getEmailVerificationBadge(user.isEmailVerified)}</td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {user.joinedAt}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewUser(user.id)}
                          className="p-1 h-8 w-8"
                          title="View User"
                        >
                          <span className="material-icons text-lg">visibility</span>
                        </Button>
                        {user.role?.toLowerCase() !== 'owner' &&
                          user.role?.toLowerCase() !== 'reseller' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDeleteUser(user.id)}
                              className="p-1 h-8 w-8"
                              title="Delete User"
                            >
                              <span className="material-icons text-lg">delete</span>
                            </Button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
